import { generateShadesAndTints, lightenHex } from '@/utils/color-generator';

const DEFAULT_PRIMARY = '#4D11F6';

const MULTIPLAI_SCALE: Record<number, string> = {
  50: '#E6E8FF',
  100: '#CACDFE',
  200: '#ABAFF5',
  300: '#7A81E9',
  400: '#5A55EB',
  500: '#4D11F6',
  600: '#3E11D8',
  700: '#2F10B1',
  800: '#1F0782',
  900: '#160754',
  950: '#0E0230',
};

/** How much white to mix into brand primary in dark mode (0–1). */
export const DARK_MODE_PRIMARY_LIGHTEN = 0.35;

const isDarkMode = (): boolean =>
  typeof document !== 'undefined' &&
  document.documentElement.getAttribute('data-mode') === 'dark';

const hexToHsl = (hex: string) => {
  const normalized = hex.replace('#', '');
  const r = parseInt(normalized.substring(0, 2), 16) / 255;
  const g = parseInt(normalized.substring(2, 4), 16) / 255;
  const b = parseInt(normalized.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }

    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
};

const hslToHex = (h: number, s: number, l: number) => {
  s /= 100;
  l /= 100;

  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) =>
    Math.round(
      255 * (l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))),
    );

  return `#${f(0).toString(16).padStart(2, '0')}${f(8)
    .toString(16)
    .padStart(2, '0')}${f(4).toString(16).padStart(2, '0')}`.toUpperCase();
};

const generateNumericShades = (color: string): Record<number, string> => {
  if (color.toUpperCase() === DEFAULT_PRIMARY) {
    return { ...MULTIPLAI_SCALE };
  }

  const hsl = hexToHsl(color);

  return {
    50: hslToHex(hsl.h, hsl.s, 95),
    100: hslToHex(hsl.h, hsl.s, 85),
    200: hslToHex(hsl.h, hsl.s, 75),
    300: hslToHex(hsl.h, hsl.s, 65),
    400: hslToHex(hsl.h, hsl.s, 55),
    500: color,
    600: hslToHex(hsl.h, hsl.s, 45),
    700: hslToHex(hsl.h, hsl.s, 35),
    800: hslToHex(hsl.h, hsl.s, 25),
    900: hslToHex(hsl.h, hsl.s, 15),
    950: hslToHex(hsl.h, hsl.s, 5),
  };
};

/** Public alias used by notification placeholders and similar UI. */
export const generatePrimaryShades = generateNumericShades;

/**
 * Resolve the brand primary for the current color mode.
 * Dark mode uses a slightly lightened primary so links/buttons stay readable
 * on gray-900 surfaces.
 */
export const resolvePrimaryForMode = (
  baseColor: string,
  dark = isDarkMode(),
): string => {
  if (!dark) {
    return baseColor;
  }

  return lightenHex(baseColor, DARK_MODE_PRIMARY_LIGHTEN);
};

/**
 * Write brand primary CSS variables on `:root`.
 * Pass the organization's base brand color (not a previously lightened value).
 */
export const applyPrimaryTheme = (baseColor: string): string => {
  const color = resolvePrimaryForMode(baseColor);
  const root = document.documentElement;
  const { tints, shades } = generateShadesAndTints(color);
  const numericShades = generateNumericShades(color);

  root.style.setProperty('--primary-color', color);
  root.style.setProperty('--primary-color-base', baseColor);

  Object.entries(tints).forEach(([index, tint]) => {
    if (tint) {
      root.style.setProperty(`--primary-color-tint-${index}`, tint);
    }
  });

  Object.entries(shades).forEach(([index, shade]) => {
    if (shade) {
      root.style.setProperty(`--primary-color-shade-${index}`, shade);
    }
  });

  Object.entries(numericShades).forEach(([index, shade]) => {
    root.style.setProperty(`--primary-color-${index}`, shade);
  });

  return color;
};
